import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Button } from '@boltenergy-be/design-system'
import { arrow, autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react'
import { SpotlightProps } from './types.ts'
import { SPOTLIGHT_CARET_GAP, SPOTLIGHT_CARET_HEIGHT, SPOTLIGHT_CARET_WIDTH } from './constants.ts'
import styles from './Spotlight.module.scss'

const Spotlight = ({ open, anchorElement, placement = 'bottom', onClose, closeLabel, children, className }: SpotlightProps) => {
  // i18n
  const { t } = useTranslation('common')

  // Local state
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)

  // Floating UI
  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
    middlewareData
  } = useFloating({
    placement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(SPOTLIGHT_CARET_GAP + SPOTLIGHT_CARET_HEIGHT),
      flip(),
      shift({ padding: SPOTLIGHT_CARET_GAP }),
      arrow({ element: arrowElement, padding: SPOTLIGHT_CARET_WIDTH })
    ]
  })

  useEffect(() => {
    refs.setReference(anchorElement)
  }, [anchorElement, refs])

  if (!open) {
    return null
  }

  // Constants
  const side = resolvedPlacement.split('-')[0] as 'top' | 'bottom'
  const caretStaticSide = side === 'top' ? 'bottom' : 'top'
  const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {}

  return (
    <FloatingPortal>
      {/* oxlint-disable-next-line react/refs -- refs.setFloating is a callback-ref setter, not a .current read; known false positive (floating-ui/floating-ui#3405) */}
      <div ref={refs.setFloating} style={floatingStyles} className={classNames(styles.spotlight, className)} data-side={side}>
        {children}

        <div
          ref={setArrowElement}
          className={styles.caret}
          style={{
            left: arrowX != null ? `${arrowX}px` : undefined,
            top: arrowY != null ? `${arrowY}px` : undefined,
            [caretStaticSide]: `-${SPOTLIGHT_CARET_HEIGHT}px`
          }}
        />

        {onClose && (
          <Button representation="link" size="small" onClick={onClose}>
            {closeLabel ?? t(($) => $.understood)}
          </Button>
        )}
      </div>
    </FloatingPortal>
  )
}

export default Spotlight
